import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { AppTableComponent, SelectedEvent } from '../../shared/table/table.component';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { EmptyCellDirective } from '../../shared/table/table.directive';
import { NgForOf, NgIf } from '@angular/common';
import { AvatarBlockModule } from '../../shared/avatar-block/avatar-block.module';
import { PageHeaderComponent } from '../../shared/page-header/page-header.component';
import { ActivatedRoute, Params, Router, RouterLink } from '@angular/router';
import { TableTypeNames } from '../../../../services/column.service';
import { ColumnMap } from '../../../../models/entities/column-map';
import { PropertyManagementCompanyService } from '../../../../services/property-management-company/property-management-company.service';
import { PropertyManagementCompanyDataService } from '../../../../services/property-management-company/property-manager-company-data.service';
import { SidenavContentService } from '../../shared/sidenav/sidenav-content/sidenav-content.service';
import { ScrollService } from '../../../../services/scroll.service';
import { GrowlerService } from '../../../../services/growler.service';
import { switchMap } from 'rxjs/operators';
import { AppWidgetGroupComponent, WidgetGroupItem } from '../../shared/widget-group/widget-group.component';
import { StatusChangeSidenavComponent, StatusChangeSidenavInput, StatusChangeSidenavOutput } from '../../shared/sidenav/status-change-sidenav/status-change-sidenav.component';
import { LegfiDatePipeModule } from '../../../../pipes/format/legfi-date.module';
import { FormSubmissionStatusRollup } from '../../../../models/entities/forms/form-submission-status';
import { FormStatus } from '../../../../enums/form-status.enum';
import { FormSubmissionFlat } from '../../../../models/entities/forms/form-submission';
import { TableFilters } from '../../../../models/entities/table-filters';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { ButtonDirective } from '../../shared/directives/button.directive';
import { PreventClickDirective } from '../../shared/directives/prevent-click.directive';
import { AppStatusChipComponent } from '../../shared/status-chip/status-chip.component';
import { MatDialogSizes } from '../../../../enums/mat-dialog-sizes.enum';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { RequestFormSubmissionApproverDialogComponent, RequestFormSubmissionApproverDialogInput } from './request-form-submission-approver-dialog/request-form-submission-approver-dialog.component';
import { RequestFormSubmissionFiltersSidenavComponent, RequestFormSubmissionFiltersSidenavInput, RequestFormSubmissionFiltersSidenavOutput } from './request-form-submission-filters-sidenav/request-form-submission-filters-sidenav.component';
import { EmptyStateModule } from '../../shared/empty-state/empty-state.module';
import { RouterButtonValue } from '../../../../models/entities/router-button-value';

@UntilDestroy()
@Component({
    selector: 'app-request-form-submissions',
    templateUrl: './request-form-submissions.component.html',
    standalone: true,
    imports: [
        AppTableComponent,
        MatSortModule,
        MatTableModule,
        EmptyCellDirective,
        NgForOf,
        AvatarBlockModule,
        PageHeaderComponent,
        AppWidgetGroupComponent,
        LegfiDatePipeModule,
        NgIf,
        RouterLink,
        MatIconModule,
        MatTooltipModule,
        MatButtonModule,
        ButtonDirective,
        PreventClickDirective,
        AppStatusChipComponent,
        MatDialogModule,
        EmptyStateModule,
    ],
})
export class RequestFormSubmissionsComponent implements OnInit, AfterViewInit
{
    @ViewChild(AppTableComponent) tableRef: AppTableComponent<FormSubmissionFlat>;
    @ViewChild(MatSort, { static: true }) columnSort: MatSort;

    pageTitle = 'Request Form Submissions';
    queryParams: Params;
    statuses: FormSubmissionStatusRollup = null;
    visibleStatuses: WidgetGroupItem[] = null;
    routerBackButton: RouterButtonValue;

    readonly formStatuses = FormStatus;
    readonly tableType = TableTypeNames.PMC_FORM_SUBMISSIONS;
    readonly columnMap: ColumnMap[] = [
        {key: 'mobile', title: 'Submission', exportAs: 'submission', isMobile: true},
        {key: 'title', title: 'Title', exportAs: 'title'},
        {key: 'status', title: 'Status', exportAs: 'status'},
        {key: 'unit', title: 'Unit', exportAs: 'unit'},
        {key: 'owner', title: 'Owner', exportAs: 'owner'},
        {key: 'submissionDate', title: 'Submission Date', exportAs: 'date'},
        {key: 'approvalsGiven', title: 'Approvals', exportAs: 'approvals'},
    ];
    readonly approvalsTooltip = 'The Approvals feature was not available at the time of this submission.';

    constructor(
            private _propertyManagementCompanyService: PropertyManagementCompanyService,
            private _propertyManagementCompanyData: PropertyManagementCompanyDataService,
            private _sidenav: SidenavContentService,
            private _dialog: MatDialog,
            private _activatedRoute: ActivatedRoute,
            private _router: Router,
            private _scrollService: ScrollService,
            private _growler: GrowlerService,
    ) {
    }

    get pmcId()  {
        return this._propertyManagementCompanyData.pmcId;
    }

    ngOnInit() {
        this._scrollService.scrollToTop();
        this.queryParams = this._activatedRoute.snapshot.queryParams;
        if (this.queryParams.hasOwnProperty('form')) {
            this.pageTitle = this.queryParams.form;
        }
        this.routerBackButton = {
            label: 'Back',
            route: `/pmc/${this.pmcId}/requests`,
            queryParams: this.queryParams,
            ariaLabel: 'Go back to request forms',
        };
    }

    ngAfterViewInit() {
        this.setupTable();
    }

    showStatuses() {
        if (this.tableRef && this.statuses) {
            const statuses = this.statuses.counts.filter((value) => value.submissionsCount > 0)
                    .map((value) => {
                        return { label: value.title, total: value.submissionsCount };
                    });
            this.visibleStatuses = [...statuses];
        }
    }

    openStatusChange(submission: FormSubmissionFlat) {
        const data: StatusChangeSidenavInput = {
            modelType: 'submissions',
            model: submission,
            statuses: this.statuses.data.filter((s) => s.formId === submission.formId),
        };

        this._sidenav.open(StatusChangeSidenavComponent, {
            data,
        }).afterClosed().subscribe((result: StatusChangeSidenavOutput) => {
            if (result?.success) {
                this.tableRef.refresh();
            }
        });
    }

    showApprovalsDialog(submission: FormSubmissionFlat) {
        const data: RequestFormSubmissionApproverDialogInput = {
            formSubmission: submission,
        };

        this._dialog.open(RequestFormSubmissionApproverDialogComponent, {
            panelClass: ['v2-dialog', 'table-layout'],
            autoFocus: 'dialog',
            width: MatDialogSizes.MD,
            data,
        });
    }

    goToRequestFormSubmission(event: SelectedEvent<FormSubmissionFlat>) {
        return this._router.navigate([event.source.id, 'details'], {
            queryParams: this.queryParams,
            relativeTo: this._activatedRoute,
        });
    }

    openFilters(filters: TableFilters) {
        const data: RequestFormSubmissionFiltersSidenavInput = {
            filters,
        };

        this._sidenav.open(RequestFormSubmissionFiltersSidenavComponent, {
            data,
        }).afterClosed().subscribe((result: RequestFormSubmissionFiltersSidenavOutput) => {
            if (result?.success) {
                return this._router.navigate([], {
                    relativeTo: this._activatedRoute,
                    queryParams: {
                        ...result.filters,
                    },
                });
            }
        });
    }

    updateSelectedStatus(widget: WidgetGroupItem) {
        if (widget.label !== this.visibleStatuses[0].label) {
            this.tableRef.addFilter({ submissionStatus: widget.label });
        } else {
            this.tableRef.removeFilter('submissionStatus');
        }
    }

    private setupTable() {
        this.tableRef.connectDataTable$.pipe(
                switchMap(() => this._propertyManagementCompanyService.getPropertyManagementRequestFormSubmissionStatuses(this.tableRef.filters)),
                switchMap((statuses) => {
                    this.statuses = statuses;
                    this.showStatuses();

                    return this._propertyManagementCompanyService.getPropertyManagementRequestFormSubmissions(
                            this.tableRef.searchValue,
                            this.columnSort.active,
                            this.columnSort.direction,
                            this.tableRef.paginator.pageIndex + 1,
                            this.tableRef.paginator.pageSize,
                            this.tableRef.filters,
                    );
                }),
                untilDestroyed(this),
        ).subscribe({
            next: (res) => this.tableRef.data$.next(res),
            error: () => {
                this.tableRef.loading = false;
                this._growler.oops('Unable to load request form submissions');
            },
        });
    }
}
